import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Toolbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShiLogo from "../../../../images/shi-logo.svg?react";
import ShiLogoDark from "../../../../images/shi-logo-dark.png";
import CommentDollarIcon from "../../../../images/comment-dollar.svg?react";
import BoxOpenIcon from "../../../../images/box-open.svg?react";
import ProductSearch from "./components/ProductSearch";
import ActionIconButton from "./components/ActionIconButton";
import UserAvatar from "./components/UserAvatar";
import { AppendIfPrefixExists } from "modules/sitePaths";
import CourtesyOrgUnitMenu from "@features/unified-user-header-feature/CourtesyOrgUnitMenu";
import CourtesyUserMenu from "@features/unified-user-header-feature/CourtesyUserMenu";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import { useClaims } from "../../../../auth/ClaimsProvider";
import ShoppingCart from "@features/unified-user-header-feature/components/ShoppingCart";
import OrganizationAvatarMenu from "./components/OrganizationAvatarMenu";
import { useGetOrgUnitNavHeader } from "@features/ecommerce/hooks/useGetOrgUnitNavHeader";
import { useUnifiedNav } from "@features/unified-side-nav-feature/context/UnifiedNavContext";
import { useTenantLogo } from "shared-ui/src/shared-features/my-acccounts/hooks/useTenantLogo";
import { useGetTenantCurrentLogo } from "@features/unified-user-header-feature/hooks/useGetTenantCurrentLogo";
import UserProfileDrawer from "@features/unified-user-header-feature/components/profile-drawers/UserProfileDrawer";
import CompanyProfileDrawer from "@features/unified-user-header-feature/components/profile-drawers/CompanyProfileDrawer";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { HALO_GRADIENT, IMPERSONATION_BAR_HEIGHT_CSS_VAR } from "@features/impersonation-feature/constants";
import { HEADER_HEIGHT_CSS_VAR } from "shared-ui/src/constants";

const MENU = {
  OrgUnit: "OrgUnit",
  User: "User",
} as const;

type MenuKey = (typeof MENU)[keyof typeof MENU];

type MenuState =
  | { menu: null; anchorEl: null }
  | { menu: MenuKey; anchorEl: HTMLElement };

const DIVIDER_STYLE = {
  mx: 1,
  height: "16px",
  alignSelf: "center",
} as const;

const TOOLBAR_HEIGHT = 60;

interface UserHeaderProps {
  displayName?: string;
  emailAddress: string;
  firstName: string;
  isShiUser: boolean;
  lastName: string;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
}

export function resolveUserDisplayName({
  displayName,
  firstName,
  lastName,
  emailAddress,
}: Pick<UserHeaderProps, "displayName" | "firstName" | "lastName" | "emailAddress">) {
  const composedName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return displayName?.trim() || composedName || emailAddress?.split("@")[0] || "Unknown User";
}

export default function CourtesyNavigationHeader({
  displayName,
  emailAddress,
  firstName,
  isShiUser,
  lastName,
}: UserHeaderProps) {
  const { hasPermission, getClaimValue } = useClaims();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const {
    toggleNav,
    isNavOpen,
    impersonationState: { collapsed: impersonationCollapsed },
  } = useUnifiedNav();

  const [menuState, setMenuState] = useState<MenuState>({
    menu: null,
    anchorEl: null,
  });
  const [profileDrawer, setProfileDrawer] = useState<null | "user" | "company">(
    null,
  );

  const accountId = getClaimValue("accountId");
  const accountName = getClaimValue("accountName");
  const tenantName = getClaimValue("tenantName");

  const tenant = useTenantLogo({
    id: accountId?.toString(),
    useGetLogo: useGetTenantCurrentLogo,
  });

  const orgUnitIdString = getClaimValue("orgUnitIdCurrent");
  const orgUnitId = orgUnitIdString ? parseInt(orgUnitIdString, 10) : undefined;

  const { data: orgUnitNavHeader, isLoading: isLoadingOrgNavHeader } =
    useGetOrgUnitNavHeader(orgUnitId ?? null, !!orgUnitId);

  const isLoadingLogo =
    tenant.isLoading || (!!orgUnitId && isLoadingOrgNavHeader);
  const organizationDisplayName = tenantName || accountName || "Organization";
  const logoUrl = tenant.logoUrl || orgUnitNavHeader?.logoUrl || undefined;

  const canViewProducts = hasPermission(ShiOnePermission.productView);
  const canViewQuotes = hasPermission(ShiOnePermission.quoteViewMy);
  const canViewOrders = hasPermission(ShiOnePermission.orderView);

  const catalogIdString = getClaimValue("compositeCatalogId");
  const catalogId = catalogIdString ? parseInt(catalogIdString, 10) : undefined;

  const onMenuClose = () => {
    setMenuState({ menu: null, anchorEl: null });
  };

  const openUserProfileDrawer = () => {
    onMenuClose();
    setProfileDrawer("user");
  };

  const openCompanyProfileDrawer = () => {
    onMenuClose();
    setProfileDrawer("company");
  };

  const handleOrgMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuState({ menu: MENU.OrgUnit, anchorEl: event.currentTarget });
  };

  const handleQuotesClick = () => navigate(AppendIfPrefixExists("/quotes"));
  const handleOrdersClick = () =>
    navigate(AppendIfPrefixExists("/orders/management"));
  const handleContactUsClick = () =>
    navigate(AppendIfPrefixExists("/my-account-team"));

  const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuState({ menu: MENU.User, anchorEl: event.currentTarget });
  };

  useEffect(() => {
    // calc() referencing the impersonation bar's own live CSS var keeps this in sync
    // without this component needing to know the bar's collapsed/pinned state.
    document.documentElement.style.setProperty(
      HEADER_HEIGHT_CSS_VAR,
      `calc(var(${IMPERSONATION_BAR_HEIGHT_CSS_VAR}, 0px) + ${TOOLBAR_HEIGHT}px)`,
    );
  }, []);

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        top: `var(${IMPERSONATION_BAR_HEIGHT_CSS_VAR}, 0px)`,
        transition: "top 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: "background.paper",
        zIndex: (theme) => theme.zIndex.appBar + 2,
      }}
    >
      {/* Full-width gradient indicator — only visible when NOT expanded (expanded bar has its own bottom gradient) */}
      {impersonationCollapsed && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: HALO_GRADIENT,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}

      <Toolbar
        variant="dense"
        sx={{
          height: `${TOOLBAR_HEIGHT}px`,
          px: 2,
          gap: 2,
          justifyContent: "space-between",
          color: "text.primary",
        }}
        disableGutters
      >
        {/* Left Section - Logo and Search Bar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 6, flex: 2 }}>
          {/* Hamburger Menu Toggle */}
          <Tooltip title="Show/Hide Menu">
            <IconButton
              onClick={toggleNav}
              size="small"
              aria-label={
                isNavOpen ? "Hide navigation menu" : "Show navigation menu"
              }
              {...telemetryProps({
                name: "header_hamburger_menu",
                section: "unified_header",
                label: "Hamburger Menu",
              })}
              sx={{ color: "text.primary", ml: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Link to={AppendIfPrefixExists("/home")}>
            {isDarkMode ? (
              <img
                src={ShiLogoDark}
                style={{ width: "auto", height: "36px" }}
                id="shiLogoHeaderButton"
                alt="SHI Logo"
              />
            ) : (
              <ShiLogo
                style={{ width: "auto", height: "36px" }}
                id={"shiLogoHeaderButton"}
              />
            )}
          </Link>

          {/* Search Bar - Show only if the user has product-view permission */}
          {canViewProducts && orgUnitId && catalogId && (
            <ProductSearch
              orgUnitId={orgUnitId}
              catalogId={catalogId}
              top={5}
            />
          )}
        </Box>

        {/* Right Section - Action Buttons and Menus */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Shopping Cart Icon Button with Badge - Show only if the user has shopping-cart-view permission */}

          <ShoppingCart />

          {/* Quote Icon Button - Show only if the user has quote-view-my permission */}
          {canViewQuotes && (
            <ActionIconButton
              icon={CommentDollarIcon}
              onClick={handleQuotesClick}
              label="View Quotes"
              telemetryName="header_quotes"
              iconSize={22}
            />
          )}

          {/* Order Icon Button - Show only if the user has order-view permission */}
          {canViewOrders && (
            <ActionIconButton
              icon={BoxOpenIcon}
              onClick={handleOrdersClick}
              label="View Orders"
              telemetryName="header_orders"
              iconSize={26}
            />
          )}

          {/* Divider before Contact Us */}
          <Divider orientation="vertical" sx={DIVIDER_STYLE} />

          {/* Contact Us Button */}
          <Button
            variant="text"
            onClick={handleContactUsClick}
            {...telemetryProps({
              name: "header_contact_us",
              section: "unified_header",
              label: "Contact Us",
            })}
            sx={{
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              color: "text.primary",
            }}
          >
            Contact Us
          </Button>

          {/* Divider after Contact Us */}
          <Divider orientation="vertical" sx={DIVIDER_STYLE} />

          {/* Organization Menu - Company Logo */}
          <OrganizationAvatarMenu
            orgName={organizationDisplayName}
            orgLogoUrl={logoUrl}
            isLoading={isLoadingLogo}
            onClick={handleOrgMenuClick}
            menuOpen={menuState.menu === MENU.OrgUnit}
          />

          {/* User Avatar */}
          {(firstName || lastName || emailAddress) && (
            <UserAvatar
              isShiUser={isShiUser}
              firstName={firstName}
              lastName={lastName}
              emailAddress={emailAddress}
              onClick={handleUserMenuClick}
              menuOpen={menuState.menu === MENU.User}
            />
          )}
        </Box>
      </Toolbar>

      {/* Courtesy Header Menu */}
      <CourtesyOrgUnitMenu
        orgUnitId={orgUnitId}
        orgLogoUrl={logoUrl}
        accountName={organizationDisplayName}
        anchorEl={menuState.anchorEl}
        open={menuState.menu === MENU.OrgUnit}
        onClose={onMenuClose}
        onOpenCompanyProfileDrawer={openCompanyProfileDrawer}
      />
      <CourtesyUserMenu
        user={
          firstName || lastName || emailAddress
            ? {
                isShiUser: isShiUser,
                firstName,
                lastName,
                email: emailAddress,
                displayName: resolveUserDisplayName({
                  displayName,
                  firstName,
                  lastName,
                  emailAddress,
                }),
              }
            : undefined
        }
        anchorEl={menuState.anchorEl}
        open={menuState.menu === MENU.User}
        onClose={onMenuClose}
        onOpenUserProfileDrawer={openUserProfileDrawer}
      />
      <UserProfileDrawer
        open={profileDrawer === "user"}
        onClose={() => setProfileDrawer(null)}
      />
      <CompanyProfileDrawer
        open={profileDrawer === "company"}
        onClose={() => setProfileDrawer(null)}
        companyLogoUrl={logoUrl}
      />
    </AppBar>
  );
}
